import React, { useState, useEffect } from 'react';
import StartMenu from './StartMenu';
import TaskbarIcon from './TaskbarIcon';
import { pinnedApps } from '../data/apps';

interface RecentApp {
  id: string;
  name: string;
  icon: string;
  isCustomIcon?: boolean;
  lastUsed: Date;
}

interface TaskbarProps {
  onAppClick: (appId: string) => void;
  recentApps: RecentApp[];
}

/**
 * Bottom taskbar with start button, pinned app icons, search box, system tray, and clock.
 * @param {TaskbarProps} props
 * @param {function} props.onAppClick - Callback invoked with an app ID when a pinned icon is clicked.
 * @param {RecentApp[]} props.recentApps - Recently used apps passed through to the start menu.
 */
const Taskbar: React.FC<TaskbarProps> = ({ onAppClick, recentApps }) => {
  const [isStartMenuOpen, setIsStartMenuOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleStartClick = () => {
    setIsStartMenuOpen(!isStartMenuOpen);
  };

  const handleCloseStartMenu = () => {
    setIsStartMenuOpen(false);
  };

  return (
    <>
      <div className="taskbar">
        <div className="taskbar-left">
          <button 
            className={`start-button ${isStartMenuOpen ? 'active' : ''}`}
            onClick={handleStartClick}
          >
            <div className="windows-logo">
              <div className="logo-square top-left"></div>
              <div className="logo-square top-right"></div>
              <div className="logo-square bottom-left"></div>
              <div className="logo-square bottom-right"></div>
            </div>
          </button>
          
          <div className="search-box">
            <div className="search-icon">🔍</div>
            <span className="search-text">Search</span>
          </div>
        </div>
        
        <div className="taskbar-center">
          {pinnedApps.map((app) => (
            <TaskbarIcon 
              key={app.id} 
              app={app} 
              onClick={() => onAppClick(app.id)}
            />
          ))}
        </div>
        
        <div className="taskbar-right">
          <div className="system-tray">
            <div className="tray-icons">
              <span>🔊</span>
              <span>📶</span>
              <span>🔋</span>
            </div>
            <div className="datetime">
              <div className="time">{currentTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
              <div className="date">{currentTime.toLocaleDateString([], {month: 'numeric', day: 'numeric', year: 'numeric'})}</div>
            </div>
          </div>
        </div>
      </div>
      
      {isStartMenuOpen && <StartMenu onClose={handleCloseStartMenu} onAppClick={onAppClick} recentApps={recentApps} />}
    </>
  );
};

export default Taskbar;